import toastManager from "../toastManager";

import {
  sanitizePairConfiguration,
  sanitizeImageDetails,
  sanitizeTags,
  appendImagesToFormDataCustomField,
  getValidPairConfiguration,
  sanitizeEntryDataResponseBase,
} from "./commonUtils";

/**
 * Sanitizes the response from the initial load of psoriasis segmentation entry data.
 * This function is used to ensure the response contains all the expected fields, and
 * to sanitize certain fields which require further processing.
 *
 * @export
 * @param {Object} params - The function parameters.
 * @param {Object} params.entryDataResponse - The raw response data from the initial load.
 * @returns {Object} The sanitized response data. It includes all the fields from the initial response,
 * but each field is checked to ensure it exists and has a valid value. Certain fields are also
 * sanitized further to ensure they have the correct format.
 *
 * The returned object includes various fields related to the psoriasis segmentation entry,
 * such as id, created date, last modified date, entry type, physician owner, patient owner,
 * celery task id and status, completed celery task id and success status, classification and semantic scores,
 * affected percentages of whole body, front and back images, PASI score and scores for different body segments,
 * original images and calculated overlays along with their details, pair configurations, thumbnail, summary,
 * notes, and tags.
 */
export const sanitizePsoriasisSegmentationEntryDataResponse = ({
  entryDataResponse,
}) => {
  const defaultValues = {
    id: "",
    created: "",
    date_modified: "",
    appointment_date: "",
    entry_type: "",
    physician_owner: "",
    patient_owner: "",

    celery_task_id: "",
    celery_task_status: "",
    completed_celery_task_id: "",
    completed_celery_task_success: false,

    classification_zc: 0.5,
    semantic_zc: 0.5,

    // Affected Percentages of whole body, front and back images.
    affected_percentage_body: 0,
    affected_percentage_front: 0,
    affected_percentage_back: 0,

    // Overall PASI score
    pasi_score: 0,

    // Data for standard PASI Scoring.
    // 1: Head.
    head_affected_percentage: 0,
    head_area_score: 0,
    head_segment_score: 0,
    head_erythema: 0,
    head_induration: 0,
    head_desquamation: 0,

    // 2: Torso / Trunk.
    torso_affected_percentage: 0,
    torso_area_score: 0,
    torso_segment_score: 0,
    torso_erythema: 0,
    torso_induration: 0,
    torso_desquamation: 0,

    // 3: Arms / Upper Extremities.
    arms_affected_percentage: 0,
    arms_area_score: 0,
    arms_segment_score: 0,
    arms_erythema: 0,
    arms_induration: 0,
    arms_desquamation: 0,

    // 4: Legs / Lower Extremities.
    legs_affected_percentage: 0,
    legs_area_score: 0,
    legs_segment_score: 0,
    legs_erythema: 0,
    legs_induration: 0,
    legs_desquamation: 0,

    // Original images
    original_image_front: "",
    image_one_0_details: "{}",
    original_image_back: "",
    image_one_1_details: "{}",

    // Calculated overlays
    overlay_front: "",
    image_two_0_details: "{}",
    overlay_back: "",
    image_two_1_details: "{}",

    pair_configuration_0: "{}",
    pair_configuration_1: "{}",

    thumbnail: "",
    summary: "",
    notes: "",
    classification_tags: [],
  };

  let sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: entryDataResponse,
    defaultValues: defaultValues,
  });

  // Fields which require further sanitization: sanitizeImageDetails, sanitizePairConfiguration,
  // and sanitizeTags functions on the relevant fields.
  sanitizedResponse.image_one_0_details = sanitizeImageDetails(
    sanitizedResponse.image_one_0_details
  );
  sanitizedResponse.image_two_0_details = sanitizeImageDetails(
    sanitizedResponse.image_two_0_details
  );
  sanitizedResponse.image_one_1_details = sanitizeImageDetails(
    sanitizedResponse.image_one_1_details
  );
  sanitizedResponse.image_two_1_details = sanitizeImageDetails(
    sanitizedResponse.image_two_1_details
  );

  sanitizedResponse.pair_configuration_0 = sanitizePairConfiguration(
    sanitizedResponse.pair_configuration_0
  );
  sanitizedResponse.pair_configuration_1 = sanitizePairConfiguration(
    sanitizedResponse.pair_configuration_1
  );

  sanitizedResponse.classification_tags = sanitizeTags(
    sanitizedResponse.classification_tags
  );

  return sanitizedResponse;
};

/**
 * Processes the sanitized data from the psoriasis segmentation entry load response and transforms it into the correct format.
 * This function ensures that the returned object adheres to a specific structure and contains all the necessary fields,
 * with correct default values if the original response didn't provide them.
 *
 * @export
 * @param {Object} params - The function parameters.
 * @param {Object} params.sanitizedEntryDataResponse - The sanitized data from the initial load response.
 * @returns {Object} The processed response data. It includes fields such as classification and semantic zoom coefficients,
 * affected percentages of whole body, front and back images, PASI score and scores for different body segments.
 *
 * The returned object has the following structure:
 * - classificationZC
 * - semanticZC
 * - affectedPercentageBody
 * - affectedPercentageFront
 * - affectedPercentageBack
 * - pasiScore
 * - headAffectedPercentage
 * - headAreaScore
 * - headSegmentScore
 * - headErythema
 * - headInduration
 * - headDesquamation
 * - torsoAffectedPercentage
 * - torsoAreaScore
 * - torsoSegmentScore
 * - torsoErythema
 * - torsoInduration
 * - torsoDesquamation
 * - armsAffectedPercentage
 * - armsAreaScore
 * - armsSegmentScore
 * - armsErythema
 * - armsInduration
 * - armsDesquamation
 * - legsAffectedPercentage
 * - legsAreaScore
 * - legsSegmentScore
 * - legsErythema
 * - legsInduration
 * - legsDesquamation
 */
export const processPsoriasisSegmentationResults = ({
  sanitizedEntryDataResponse,
}) => {
  // Define the default values for the properties
  const defaultValues = {
    // Zoom coefficients
    classification_zc: 0.5,
    semantic_zc: 0.5,

    // Affected Percentages of whole body, front and back images.
    affected_percentage_body: 0,
    affected_percentage_front: 0,
    affected_percentage_back: 0,

    // Overall PASI score
    pasi_score: 0,

    // Data for standard PASI Scoring.
    // 1: Head.
    head_affected_percentage: 0,
    head_area_score: 0,
    head_segment_score: 0,
    head_erythema: 0,
    head_induration: 0,
    head_desquamation: 0,

    // 2: Torso / Trunk.
    torso_affected_percentage: 0,
    torso_area_score: 0,
    torso_segment_score: 0,
    torso_erythema: 0,
    torso_induration: 0,
    torso_desquamation: 0,

    // 3: Arms / Upper Extremities.
    arms_affected_percentage: 0,
    arms_area_score: 0,
    arms_segment_score: 0,
    arms_erythema: 0,
    arms_induration: 0,
    arms_desquamation: 0,

    // 4: Legs / Lower Extremities.
    legs_affected_percentage: 0,
    legs_area_score: 0,
    legs_segment_score: 0,
    legs_erythema: 0,
    legs_induration: 0,
    legs_desquamation: 0,
  };

  // Use the sanitizeEntryDataResponseBase function to process the data
  const sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: sanitizedEntryDataResponse,
    defaultValues: defaultValues,
  });

  // Return the processed data
  return {
    classificationZC: sanitizedResponse.classification_zc,
    semanticZC: sanitizedResponse.semantic_zc,

    // # Affected Percentages of whole body, front and back images.
    affectedPercentageBody: sanitizedResponse.affected_percentage_body,
    affectedPercentageFront: sanitizedResponse.affected_percentage_front,
    affectedPercentageBack: sanitizedResponse.affected_percentage_back,

    // # Overall PASI score
    pasiScore: sanitizedResponse.pasi_score,

    // # Data for standard PASI Scoring.
    // # 1: Head.
    headAffectedPercentage: sanitizedResponse.head_affected_percentage,
    headAreaScore: sanitizedResponse.head_area_score,
    headSegmentScore: sanitizedResponse.head_segment_score,
    headErythema: sanitizedResponse.head_erythema,
    headInduration: sanitizedResponse.head_induration,
    headDesquamation: sanitizedResponse.head_desquamation,

    // # 2: Torso / Trunk.
    torsoAffectedPercentage: sanitizedResponse.torso_affected_percentage,
    torsoAreaScore: sanitizedResponse.torso_area_score,
    torsoSegmentScore: sanitizedResponse.torso_segment_score,
    torsoErythema: sanitizedResponse.torso_erythema,
    torsoInduration: sanitizedResponse.torso_induration,
    torsoDesquamation: sanitizedResponse.torso_desquamation,

    // # 3: Arms / Upper Extremities.
    armsAffectedPercentage: sanitizedResponse.arms_affected_percentage,
    armsAreaScore: sanitizedResponse.arms_area_score,
    armsSegmentScore: sanitizedResponse.arms_segment_score,
    armsErythema: sanitizedResponse.arms_erythema,
    armsInduration: sanitizedResponse.arms_induration,
    armsDesquamation: sanitizedResponse.arms_desquamation,

    // # 4: Legs / Lower Extremities.
    legsAffectedPercentage: sanitizedResponse.legs_affected_percentage,
    legsAreaScore: sanitizedResponse.legs_area_score,
    legsSegmentScore: sanitizedResponse.legs_segment_score,
    legsErythema: sanitizedResponse.legs_erythema,
    legsInduration: sanitizedResponse.legs_induration,
    legsDesquamation: sanitizedResponse.legs_desquamation,
  };
};

/**
 * Prepares the form data for the psoriasis segmentation calculation process.
 * This function is responsible for checking the integrity of the input data, appending necessary fields into the FormData,
 * and managing user feedback in case of invalid inputs.
 *
 * @export
 * @param {Object} params - The function parameters.
 * @param {FormData} params.formData - The FormData object to be populated with the calculation data.
 * @param {Array} params.comparisonItems - An array of objects representing the comparison items containing front and back images.
 * @param {number} params.classificationZC - The classification zoom coefficient.
 * @param {number} params.semanticZC - The semantic zoom coefficient.
 * @returns {boolean} Returns true if the processing is successful, false otherwise. If there are no images in the comparison item,
 * it will display a warning message and return false.
 *
 * The populated FormData will have the following fields:
 * - original_image_front
 * - image_one_0_details
 * - original_image_back
 * - image_one_1_details
 * - classification_zc
 * - semantic_zc
 */
export const processPsoriasisSegmentationCalculationFormData = ({
  formData,
  comparisonItems,
  classificationZC,
  semanticZC,
}) => {
  const { fileOne: fileOneFront, fileOneDetails: fileOneDetailsFront } =
    comparisonItems[0];
  const { fileOne: fileOneBack, fileOneDetails: fileOneDetailsBack } =
    comparisonItems[1];

  // If there are no images in any items, display a warning and abort the processing
  if (fileOneFront.length === 0 && fileOneBack.length === 0) {
    toastManager(
      "warning",
      "Calculation Warning",
      "Cannot calculate entry with no input images. Add images to begin the calculation."
    );
    // Return false to indicate an issue during processing
    return false;
  }

  // Populate FormData with the images and details if the image is present
  fileOneFront.length !== 0 &&
    appendImagesToFormDataCustomField({
      formData: formData,
      fileInput: fileOneFront,
      fileDetails: fileOneDetailsFront,
      fileName: "original_image_front",
      detailsName: "image_one_0_details",
    });

  fileOneBack.length !== 0 &&
    appendImagesToFormDataCustomField({
      formData: formData,
      fileInput: fileOneBack,
      fileDetails: fileOneDetailsBack,
      fileName: "original_image_back",
      detailsName: "image_one_1_details",
    });

  // Append the coefficients to FormData
  formData.append("classification_zc", classificationZC);
  formData.append("semantic_zc", semanticZC);

  // Return true to indicate successful processing
  return true;
};

/**
 * Prepares the form data for the psoriasis segmentation save operation.
 * This function is responsible for retrieving valid configurations for comparison items,
 * and appending necessary fields into the FormData.
 *
 * @export
 * @param {Object} params - The function parameters.
 * @param {FormData} params.formData - The FormData object to be populated with the save data.
 * @param {Array} params.comparisonItems - An array of objects representing the comparison items containing front and back images.
 *
 * The populated FormData will have the following fields:
 * - pair_configuration_0
 * - pair_configuration_1
 * - original_image_front
 * - image_one_0_details
 * - overlay_front
 * - image_two_0_details
 * - original_image_back
 * - image_one_1_details
 * - overlay_back
 * - image_two_1_details
 *
 * Note: This function does not append images to the FormData. It only appends the image details.
 */
export const processPsoriasisSegmentationSaveFormData = ({
  formData,
  comparisonItems,
}) => {
  // Retrieve the valid configuration for the first comparison item
  const validPairConfigurationFront = getValidPairConfiguration({
    comparisonItem: comparisonItems[0],
  });

  const validPairConfigurationBack = getValidPairConfiguration({
    comparisonItem: comparisonItems[1],
  });

  const {
    fileOne: fileOneFront,
    fileTwo: fileTwoFront,
    fileOneDetails: fileOneDetailsFront,
    fileTwoDetails: fileTwoDetailsFront,
  } = comparisonItems[0];

  const {
    fileOne: fileOneBack,
    fileTwo: fileTwoBack,
    fileOneDetails: fileOneDetailsBack,
    fileTwoDetails: fileTwoDetailsBack,
  } = comparisonItems[1];

  // Append the valid configuration to FormData
  if (fileOneFront.length !== 0 && fileTwoFront.length !== 0) {
    formData.append(
      `pair_configuration_${0}`,
      JSON.stringify(validPairConfigurationFront)
    );
  }
  if (fileOneBack.length !== 0 && fileTwoBack.length !== 0) {
    formData.append(
      `pair_configuration_${1}`,
      JSON.stringify(validPairConfigurationBack)
    );
  }

  // Append the details of the  images to FormData without including the images
  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileOneFront,
    fileDetails: fileOneDetailsFront,
    fileName: "original_image_front",
    detailsName: "image_one_0_details",
    appendImage: false,
  });

  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileTwoFront,
    fileDetails: fileTwoDetailsFront,
    fileName: "overlay_front",
    detailsName: "image_two_0_details",
    appendImage: false,
  });

  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileOneBack,
    fileDetails: fileOneDetailsBack,
    fileName: "original_image_back",
    detailsName: "image_one_1_details",
    appendImage: false,
  });

  appendImagesToFormDataCustomField({
    formData: formData,
    fileInput: fileTwoBack,
    fileDetails: fileTwoDetailsBack,
    fileName: "overlay_back",
    detailsName: "image_two_1_details",
    appendImage: false,
  });
};

/**
 * Extracts and sanitizes the psoriasis segmentation calculation results from the response received from celery.
 * This function destructures the response object and provides default values for each property in case they are undefined.
 *
 * @export
 * @param {Object} params - The function parameters.
 * @param {Object} params.psoriasisSegmentationCalculationResultResponse - The response object received from celery, containing psoriasis segmentation calculation results.
 *
 * @returns {Object} An object with sanitized psoriasis segmentation calculation results. The properties of this object include:
 * - Zoom coefficients (classification_zc, semantic_zc)
 * - Affected percentages of whole body, front and back images (affected_percentage_body, affected_percentage_front, affected_percentage_back)
 * - Overall PASI score (pasi_score)
 * - Data for standard PASI Scoring for different body parts (head, torso, arms, legs)
 * - Overlays (overlay_front, overlay_back)
 *
 * If a property is undefined in the response, this function will assign a default value to it:
 * - For numeric properties, the default value is 0 or 0.5 (for zoom coefficients)
 * - For string properties (overlays), the default value is an empty string
 */
export const sanitizePsoriasisSegmentationCalculationResultResponse = ({
  psoriasisSegmentationCalculationResultResponse = {},
}) => {
  const {
    // Zoom coefficients
    classification_zc,
    semantic_zc,

    // Affected Percentages of whole body, front and back images.
    affected_percentage_body,
    affected_percentage_front,
    affected_percentage_back,

    // Overall PASI score
    pasi_score,

    // Data for standard PASI Scoring.
    // 1: Head.
    head_affected_percentage,
    head_area_score,
    head_segment_score,
    head_erythema,
    head_induration,
    head_desquamation,

    // 2: Torso / Trunk.
    torso_affected_percentage,
    torso_area_score,
    torso_segment_score,
    torso_erythema,
    torso_induration,
    torso_desquamation,

    // 3: Arms / Upper Extremities.
    arms_affected_percentage,
    arms_area_score,
    arms_segment_score,
    arms_erythema,
    arms_induration,
    arms_desquamation,

    // 4: Legs / Lower Extremities.
    legs_affected_percentage,
    legs_area_score,
    legs_segment_score,
    legs_erythema,
    legs_induration,
    legs_desquamation,

    overlay_front,
    overlay_back,
  } = psoriasisSegmentationCalculationResultResponse;

  return {
    // Zoom coefficients
    classification_zc:
      classification_zc !== undefined ? classification_zc : 0.5,
    semantic_zc: semantic_zc !== undefined ? semantic_zc : 0.5,

    // Affected Percentages of whole body, front and back images.
    affected_percentage_body:
      affected_percentage_body !== undefined ? affected_percentage_body : 0,
    affected_percentage_front:
      affected_percentage_front !== undefined ? affected_percentage_front : 0,
    affected_percentage_back:
      affected_percentage_back !== undefined ? affected_percentage_back : 0,

    // Overall PASI score
    pasi_score: pasi_score !== undefined ? pasi_score : 0,

    // Data for standard PASI Scoring.
    // 1: Head.
    head_affected_percentage:
      head_affected_percentage !== undefined ? head_affected_percentage : 0,
    head_area_score: head_area_score !== undefined ? head_area_score : 0,
    head_segment_score:
      head_segment_score !== undefined ? head_segment_score : 0,
    head_erythema: head_erythema !== undefined ? head_erythema : 0,
    head_induration: head_induration !== undefined ? head_induration : 0,
    head_desquamation: head_desquamation !== undefined ? head_desquamation : 0,

    // 2: Torso / Trunk.
    torso_affected_percentage:
      torso_affected_percentage !== undefined ? torso_affected_percentage : 0,
    torso_area_score: torso_area_score !== undefined ? torso_area_score : 0,
    torso_segment_score:
      torso_segment_score !== undefined ? torso_segment_score : 0,
    torso_erythema: torso_erythema !== undefined ? torso_erythema : 0,
    torso_induration: torso_induration !== undefined ? torso_induration : 0,
    torso_desquamation:
      torso_desquamation !== undefined ? torso_desquamation : 0,

    // 3: Arms / Upper Extremities.
    arms_affected_percentage:
      arms_affected_percentage !== undefined ? arms_affected_percentage : 0,
    arms_area_score: arms_area_score !== undefined ? arms_area_score : 0,
    arms_segment_score:
      arms_segment_score !== undefined ? arms_segment_score : 0,
    arms_erythema: arms_erythema !== undefined ? arms_erythema : 0,
    arms_induration: arms_induration !== undefined ? arms_induration : 0,
    arms_desquamation: arms_desquamation !== undefined ? arms_desquamation : 0,

    // 4: Legs / Lower Extremities.
    legs_affected_percentage:
      legs_affected_percentage !== undefined ? legs_affected_percentage : 0,
    legs_area_score: legs_area_score !== undefined ? legs_area_score : 0,
    legs_segment_score:
      legs_segment_score !== undefined ? legs_segment_score : 0,
    legs_erythema: legs_erythema !== undefined ? legs_erythema : 0,
    legs_induration: legs_induration !== undefined ? legs_induration : 0,
    legs_desquamation: legs_desquamation !== undefined ? legs_desquamation : 0,

    // Overlays
    overlay_front: overlay_front !== undefined ? overlay_front : "",
    overlay_back: overlay_back !== undefined ? overlay_back : "",
  };
};
